import React, { useEffect, useState } from "react";
import { BookingButton } from "../../Components/BookingButton/BookingButton";
import { HeroViewModel } from "./HeroViewModel.csharp";
import { MediaType } from "../Media/MediaType.csharp";
import classNames from "classnames";
import { GtmService } from "../../Components/Booking/GtmService";
import { HeroHeight } from "../../../Model/HeroHeight.csharp";

export const Hero: React.FC<HeroViewModel> = ({
  mediaViewModel,
  imageAnchorPosition,
  videoAnchorStyle,
  flipImage,
  heading,
  intro,
  topLine,
  byline,
  price,
  bookingCta,
  showBookingCta,
  removeBackgroundColor,
  headingFontColor,
  optimizelyExperimentation: OptimizelyExperimentation,
  heroHeight,
}) => {
  const headingHtml = heading.replace("|", "\u00AD");
  const headingStyle = headingFontColor ? { color: headingFontColor } : {};

  const [initialIntro, setInitialIntro] = useState("");

  useEffect(() => {
    setInitialIntro(intro);
  }, []);

  return (
    <>
      <div
        id="hero-section"
        className={classNames("hero theme-inherit mb-4", {
          "hero__remove-background-color": removeBackgroundColor,
          "hero__medium-height": heroHeight === HeroHeight.Medium,
          "hero__large-height": heroHeight === HeroHeight.Large,
        })}
      >
        {mediaViewModel?.mediaUrl ? (
          <>
            {mediaViewModel.mediaType === MediaType.Image && (
              <div className={classNames("hero__media theme-inherit-medium")}>
                <picture className="hero__media-img">
                  <source
                    media="(max-width: 767px)"
                    srcSet={`${
                      mediaViewModel.mediaUrl
                    }?width=768&height=384&rmode=crop&ranchor=${
                      imageAnchorPosition || "center"
                    }`}
                  />
                  <source
                    media="(min-width: 768px) and (max-width: 991px)"
                    srcSet={`${
                      mediaViewModel.mediaUrl
                    }?width=1000&height=333&rmode=crop&ranchor=${
                      imageAnchorPosition || "center"
                    }`}
                  />
                  <source
                    media="(min-width: 992px)"
                    srcSet={`${
                      mediaViewModel.mediaUrl
                    }?width=1440&height=500&rmode=crop&ranchor=${
                      imageAnchorPosition || "center"
                    }`}
                  />
                  <img
                    src={`${
                      mediaViewModel.mediaUrl
                    }?width=700&height=500&rmode=crop&ranchor=${
                      imageAnchorPosition || "center"
                    }`}
                    alt=""
                    style={{
                      width: "100%",
                      objectPosition: imageAnchorPosition || "center",
                    }}
                  />
                </picture>
              </div>
            )}

            {mediaViewModel.mediaType === MediaType.Video && (
              <div className={classNames("hero__media theme-inherit-medium")}>
                <video
                  className="hero__media-img hero__media-video"
                  style={
                    typeof videoAnchorStyle === "string" ? {} : videoAnchorStyle
                  }
                  autoPlay
                  muted
                  playsInline
                  loop
                >
                  <source src={mediaViewModel.mediaUrl} type="video/webm" />
                  <source src={mediaViewModel.mediaUrl} type="video/mp4" />
                </video>
              </div>
            )}

            {mediaViewModel.mediaType === MediaType.Icon && (
              <>
                <div
                  className={classNames(
                    "hero__media theme-inherit-medium d-none d-md-flex d-flex justify-content-center align-items-center"
                  )}
                >
                  <img
                    src={mediaViewModel.mediaUrl}
                    alt=""
                    className="hero__media-icon"
                    style={{
                      display: "flex",
                      alignSelf: "anchor-center",
                    }}
                  />
                </div>
              </>
            )}
          </>
        ) : (
          <div className="hero__media hero__media--no-image theme-inherit-medium"></div>
        )}

        <div className={classNames("hero__text-container")}>
          <div
            id="hero-text-inner"
            className={classNames(
              "hero__text-container-inner theme-inherit-hero-light"
            )}
          >
            <div className="d-flex flex-column">
              <h1 className="hero__heading mb-0">
                {topLine && <span className="fw-normal">{topLine}</span>}
                <span
                  className="main"
                  style={headingStyle}
                  dangerouslySetInnerHTML={{ __html: headingHtml }}
                />
                {byline && (
                  <span className="fw-normal" style={headingStyle}>
                    {byline}
                  </span>
                )}
              </h1>

              {showBookingCta && bookingCta?.bookingButton && (
                <div
                  className="edge-to-edge-sm col-lg-4 p-sm-0 hero-booking-button"
                >
                  <BookingButton
                    bookingButton={bookingCta.bookingButton}
                    variant="hero"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {intro && (
        <div className="container-fluid mt-n3">
          <div className="offset-lg-up">
            <div className="body-text-container mb-3 mb-md-4 lead">
              <p
                dangerouslySetInnerHTML={{
                  __html: initialIntro,
                }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
